/**
 * @module components/analysis/AnalysisResults
 * @description
 *   Composes the full analysis results view: back/export actions, score
 *   grid, AI review, music-only hit potential section, similar works, a
 *   processing time note, and the feedback form.
 *
 * @extracted-from client/src/App.js (lines ~2220–2804, Phase 7)
 */

import React from 'react';
import ScoreGrid from './ScoreGrid';
import ReviewSection from './ReviewSection';
import HitPotentialSection from './HitPotentialSection';
import SimilarWorksSection from './SimilarWorksSection';
import { Button, Card, SectionHeader } from '../ui';

/**
 * @param {object} props
 * @param {object} props.results
 * @param {'music'|'book'} props.craftType
 * @param {boolean} props.showPremiumContent
 * @param {boolean} [props.hasPremiumAccess]
 * @param {() => void} props.onBack
 * @param {() => void} [props.onExportPdf]
 */
export default function AnalysisResults({
  results,
  craftType,
  showPremiumContent,
  hasPremiumAccess,
  onBack,
  onExportPdf,
}) {
  const handleExportPdf = () => {
    if (onExportPdf) {
      onExportPdf();
      return;
    }
    window.print();
  };

  return (
    <div className="flex flex-col gap-10 animate-fade-in">
      <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
        <Button
          variant="secondary"
          onClick={onBack}
          className="flex items-center space-x-3 text-lg font-semibold"
        >
          <span className="text-2xl">←</span>
          <span>Analyze Another {craftType === 'music' ? 'Track' : 'Book'}</span>
        </Button>

        <Button
          variant="primary"
          onClick={handleExportPdf}
          className="flex items-center space-x-3 text-lg font-bold py-4 px-8"
        >
          <span className="text-xl">📄</span>
          <span>Generate PDF</span>
        </Button>
      </div>

      {/* Major analysis cards — shared gap so collapsed/expanded spacing stays even */}
      <div className="flex flex-col gap-10">
        <ScoreGrid results={results} craftType={craftType} showPremiumContent={showPremiumContent} />
        <ReviewSection results={results} craftType={craftType} />
        <HitPotentialSection
          results={results}
          craftType={craftType}
          showPremiumContent={showPremiumContent}
        />
        <SimilarWorksSection
          results={results}
          craftType={craftType}
          showPremiumContent={showPremiumContent}
        />
      </div>

      {results.processingTime && (
        <Card variant="inset" className="text-center">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-3 h-3 bg-apple-success rounded-full animate-pulse" aria-hidden />
            <p className="text-critic-text-secondary text-sm font-normal">
              Analysis completed in{' '}
              <span className="text-apple-success font-semibold tabular-nums">
                {(results.processingTime / 1000).toFixed(1)}s
              </span>
            </p>
          </div>
        </Card>
      )}

      <Card className="animate-slide-up glow-effect-blue hover-lift">
        <SectionHeader
          stacked={false}
          icon={<span className="text-white font-bold text-lg">💬</span>}
          gradient="green"
          title="Help Us Improve"
          subtitle="Share feedback — we send a code for bonus analyses."
        />

        <div className="bg-gradient-to-r from-apple-green/10 to-apple-teal/10 backdrop-blur-xl rounded-2xl border border-apple-green/20 p-6">
          <div className="mb-6 text-center">
            <p className="text-critic-text-secondary text-lg mb-4">
              We'd love to hear your thoughts! Share detailed feedback and receive a promo code for{' '}
              <span className="text-apple-green font-bold">5 FREE analyses</span>.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 text-sm text-critic-text-muted">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-apple-green rounded-full" />
                <span>Enter your {craftType === 'music' ? 'artist' : 'author'} name</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-apple-green rounded-full" />
                <span>Provide your email</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-apple-green rounded-full" />
                <span>Share detailed feedback (15+ words)</span>
              </div>
            </div>
          </div>

          <div className="w-full h-96 sm:h-[500px] rounded-xl overflow-hidden">
            <iframe
              src="https://forms.gle/kcvzCVDTRvVAGZEG8"
              width="100%"
              height="100%"
              frameBorder="0"
              marginHeight="0"
              marginWidth="0"
              title="CriticAI Feedback Form"
              className="bg-white rounded-xl"
            >
              Loading…
            </iframe>
          </div>

          <div className="mt-6 text-center">
            <div className="mt-4 flex items-center justify-center space-x-2">
              <span className="text-critic-text-muted text-xs font-normal">Powered by</span>
              <span className="text-critic-text-secondary text-xs font-medium">Google Forms</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
